/* GT3Designs homepage motion, powered by GSAP + ScrollTrigger.

   Fail-safe by design: the hidden "from" states are applied ONLY here, via GSAP.
   If GSAP fails to load (CDN down, blocked, etc.) this file returns early and the
   page renders as a normal, fully visible static page — nothing is ever left
   permanently hidden. Respects prefers-reduced-motion (no movement at all).

   Taste: power3.out easing, opacity + small y, ~0.06s stagger, marketing-length
   durations, restraint. No bounce, no elastic. */
(function () {
  'use strict';

  var gsap = window.gsap;
  // Fail-safe: no GSAP -> leave the page fully visible.
  if (!gsap) return;

  var ScrollTrigger = window.ScrollTrigger;
  if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  // Direct-child reveal targets of a group (skip nested reveal groups so each
  // group only animates its own row of items).
  function childTargets(group) {
    var out = [];
    var kids = group.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 1 && !kids[i].hasAttribute('data-gt3d-reveal')) {
        out.push(kids[i]);
      }
    }
    return out;
  }

  function start() {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var root = document.documentElement;
    // GSAP is here: cancel the FOUC fallback so the hidden hero state persists
    // until we animate it in. (If GSAP had failed, start() never runs and the
    // inline fallback un-hides the hero after ~1s instead.)
    if (window.__gt3dMotionFallback) {
      clearTimeout(window.__gt3dMotionFallback);
      window.__gt3dMotionFallback = null;
    }
    var motionActive = root.classList.contains('gsap-motion');

    var hero = document.querySelector('.gt3dhero');
    var heroItems = hero
      ? hero.querySelectorAll(
          '.gt3dhero__eyebrow, .gt3dhero__title, .gt3dhero__sub, .gt3dhero__cta-row, .gt3dhero__dim'
        )
      : [];
    var heroImage = hero ? hero.querySelector('.gt3dhero__img') : null;

    var groups = document.querySelectorAll('[data-gt3d-reveal]');

    // Reduced motion: pin everything to its final, visible state. No reveals,
    // no transforms, no hero timeline.
    if (reduceMotion) {
      // No movement: drop the FOUC gate and pin everything to its visible state.
      root.classList.remove('gsap-motion');
      if (heroItems.length) gsap.set(heroItems, { opacity: 1, y: 0, clearProps: 'all' });
      if (heroImage) gsap.set(heroImage, { opacity: 1, clearProps: 'all' });
      for (var r = 0; r < groups.length; r++) {
        var targets = childTargets(groups[r]);
        if (targets.length) gsap.set(targets, { opacity: 1, y: 0, clearProps: 'all' });
      }
      return;
    }

    // Smooth scroll (Lenis), synced with ScrollTrigger so reveals stay in step.
    // Only runs when motion is allowed (reduced-motion returned above) and Lenis
    // actually loaded; otherwise the page uses native scrolling.
    if (window.Lenis && ScrollTrigger) {
      var lenis = new window.Lenis({ lerp: 0.1, smoothWheel: true });
      // Drive Lenis from gsap.ticker (Lenis 1.3 has no internal rAF), and keep
      // ScrollTrigger updated on every Lenis scroll.
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);

      // In-page anchors (e.g. the hero "Find your fit" -> #find-your-fit) smooth-
      // scroll via Lenis. The skip-to-content link is left native so its focus
      // jump keeps working for keyboard / assistive tech.
      document.addEventListener('click', function (event) {
        var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
        if (!link || link.classList.contains('skip-to-content-link')) return;
        var hash = link.getAttribute('href');
        if (!hash || hash === '#') return;
        var target = document.getElementById(hash.slice(1));
        if (!target) return;
        event.preventDefault();
        var headerH = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
        lenis.scrollTo(target, { offset: isNaN(headerH) ? 0 : -headerH });
      });
    }

    // Hero text: deliberate staggered entrance, exactly once, now (DOM ready).
    // It does not wait for the image and is never re-run. If the FOUC fallback
    // already fired (slow GSAP), the hero is already visible, so just confirm it.
    if (heroItems.length) {
      if (motionActive) {
        gsap.fromTo(
          heroItems,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.16 }
        );
      } else {
        gsap.set(heroItems, { opacity: 1, y: 0, clearProps: 'all' });
      }
    }

    // Hero image: a single, smooth fade, started ONLY once the image has fully
    // loaded AND decoded, so the real pixels are ready before the fade begins
    // (no fade-then-pop). Independent of the text; never blocks or restarts it.
    if (heroImage) {
      if (motionActive) {
        var fadeImage = function () {
          gsap.to(heroImage, { opacity: 1, duration: 0.7, ease: 'power3.out' });
        };
        if (typeof heroImage.decode === 'function') {
          // decode() resolves only when load + decode are done, whether the
          // image is still loading or already cached. Fade in anyway if it
          // rejects, so the image is never left hidden.
          heroImage.decode().then(fadeImage).catch(fadeImage);
        } else if (heroImage.complete && heroImage.naturalWidth > 0) {
          fadeImage();
        } else {
          heroImage.addEventListener('load', fadeImage, { once: true });
          heroImage.addEventListener(
            'error',
            function () { gsap.set(heroImage, { opacity: 1 }); },
            { once: true }
          );
        }
      } else {
        gsap.set(heroImage, { opacity: 1, clearProps: 'all' });
      }
    }

    // Sections: reveal each group's items as it scrolls in (and on load for
    // anything already in view). Plays once.
    for (var g = 0; g < groups.length; g++) {
      (function (group) {
        var items = childTargets(group);
        if (!items.length) return;
        gsap.from(items, {
          opacity: 0,
          y: 24,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.06,
          // Remove the inline transform/opacity GSAP leaves behind once the
          // reveal finishes, so CSS :hover transforms (card lifts) aren't blocked
          // by a lingering inline transform.
          clearProps: 'transform,opacity',
          scrollTrigger: {
            trigger: group,
            start: 'top 80%',
            once: true
          }
        });
      })(groups[g]);
    }

    // Recalculate trigger positions once images (which affect layout height)
    // have loaded, so reveals fire at the right scroll point.
    if (ScrollTrigger) {
      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
